import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ModalPortal } from './ui';
import type { QueueEvidence } from '../types';

function isImage(file: QueueEvidence): boolean {
  return Boolean(file.mime_type?.startsWith('image/')) || /\.(jpe?g|png|webp|gif)$/i.test(file.file_name);
}

function isPdf(file: QueueEvidence): boolean {
  return file.mime_type === 'application/pdf' || file.file_name.toLowerCase().endsWith('.pdf');
}

interface EvidenceViewerProps {
  files: QueueEvidence[];
  title?: string;
  itemHref?: string | null;
  onClose: () => void;
}

export const EvidenceViewer: React.FC<EvidenceViewerProps> = ({
  files,
  title,
  itemHref,
  onClose,
}) => {
  const [index, setIndex] = useState(0);
  const file = files[index];
  if (!file) return null;

  return (
    <ModalPortal>
    <div
      className="fixed inset-0 bg-black/75 z-[80] flex items-center justify-center p-4 print:hidden"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 truncate">{file.file_name}</p>
            {title ? <p className="text-xs text-gray-400 mt-0.5 truncate">{title}</p> : null}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {itemHref && (
              <Link
                to={itemHref}
                className="text-sm text-brand-600 hover:text-brand-800 font-medium"
              >
                Open item
              </Link>
            )}
            <a
              href={file.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-brand-600 hover:text-brand-800 font-medium"
            >
              Open file
            </a>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {files.length > 1 && (
          <div className="flex gap-2 px-5 py-2 border-b border-gray-100 overflow-x-auto">
            {files.map((entry, i) => (
              <button
                key={entry.id}
                type="button"
                onClick={() => setIndex(i)}
                className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${
                  i === index
                    ? 'bg-brand-100 text-brand-800 font-semibold'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {entry.file_name}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-auto p-4 bg-gray-50 flex items-center justify-center min-h-64">
          {isImage(file) ? (
            <img
              src={file.file_url}
              alt={file.file_name}
              className="max-w-full max-h-[70vh] object-contain rounded-xl shadow"
            />
          ) : isPdf(file) ? (
            <iframe src={file.file_url} className="w-full h-[70vh] rounded-lg border" title={file.file_name} />
          ) : (
            <a
              href={file.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700"
            >
              Download file
            </a>
          )}
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};
